import React, { useState } from 'react'
import cn from 'classnames'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import { ChevronUp } from 'react-ionicons'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { AutoLink, Container } from '@/components/utils'
import { IonIcon } from '@/src/icons'
import { Divider } from '@/components/elements/Divider'

export interface Item {
  question: string
  answer: string[]
}

export interface FaqProps {
  title: string
  subtitle: string
  items: Item[]
}

const itemVariants: Variants = {
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
  enter: {
    height: 'auto',
    opacity: 1,
    transition: {
      duration: 0.2,
    },
  },
}

export const FaqItem: React.FC<{
  onClick?: () => void
  item: Item
  last?: boolean
  selected: boolean
}> = ({
  item: { question, answer },
  selected,
  last = false,
  onClick = () => null,
}) => {
  return (
    <div>
      <button type="button" onClick={onClick} className="block w-full">
        <div className="flex">
          <h3
            className={cn(
              'flex-1 text-left text-2xl font-display font-black mb-2 transition-colors',
              {
                'text-navy-900': !selected,
                'text-orange': selected,
              }
            )}
          >
            {question}
          </h3>
          <motion.span
            className="flex items-center justify-center ml-2 h-8"
            initial={{ rotateZ: '-180deg' }}
            animate={selected ? { rotateZ: 0 } : { rotateZ: '-180deg' }}
          >
            <IonIcon
              icon={ChevronUp}
              className={cn('w-6 h-4 text-orange', {
                'text-navy-light-700': !selected,
                'text-orange': selected,
              })}
            />
          </motion.span>
        </div>
      </button>
      <AnimatePresence exitBeforeEnter initial={false}>
        {selected &&
          answer.map((answ) => (
            <motion.p
              variants={itemVariants}
              className="py-2"
              style={{ transformOrigin: 'top center' }}
              initial="exit"
              animate="enter"
              exit="exit"
              key={answer.indexOf(answ)}
            >
              <AutoLink
                text={answ}
                linkText="(Enllaç)"
                className="hover:text-orange"
              />
            </motion.p>
          ))}
      </AnimatePresence>
      {!last && <Divider className="mb-6 mt-6 opacity-50" />}
    </div>
  )
}

export const Faq: React.FC<FaqProps> = ({ title, subtitle, items }) => {
  const [selectedItem, setSelectedItem] = useState<Item | null>(items[0])

  return (
    <div className="my-20">
      <Container narrow>
        <SectionTitle title={title} subtitle={subtitle} />
        {items.map((item, index) => (
          <FaqItem
            item={item}
            selected={item === selectedItem}
            last={index === items.length - 1}
            key={item.question}
            onClick={() =>
              item !== selectedItem
                ? setSelectedItem(item)
                : setSelectedItem(null)
            }
          />
        ))}
      </Container>
    </div>
  )
}
